import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function AmadeoPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Hand Rehabilitation Robot
            </span>
            <h1 className="text-5xl font-bold text-gray-900 mt-3 mb-6">
              Amadeo Hand Therapy System
            </h1>
          </div>
          
          <div className="mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/8c1fd297b7689b6330a1340db7adecd7.jpg"
              alt="Amadeo Hand Therapy System"
              className="w-full rounded-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              The Amadeo is an advanced robotic device specifically designed for finger and hand rehabilitation. It provides automated, computer-controlled therapy for patients recovering from stroke, traumatic brain injury, spinal cord injury, and other neurological conditions affecting hand function.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
              What Makes Amadeo Unique
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Each finger can be moved independently or in combination, allowing for highly specific and targeted therapy tailored to individual patient needs. The system supports both passive movement for severely impaired patients and active-assisted training as function improves, ensuring appropriate intervention at every stage of recovery.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Engaging therapy games and exercises make rehabilitation more motivating and enjoyable, improving patient compliance and outcomes. The interactive nature of the system transforms repetitive exercises into goal-oriented activities that maintain patient interest throughout the treatment program.
            </p>

            <p className="text-gray-700 leading-relaxed mb-12">
              Comprehensive data collection and analysis tools allow therapists to monitor patient progress and adjust treatment protocols accordingly. Built-in safety mechanisms prevent excessive force and ensure patient comfort throughout the therapy session, while customizable programs enable therapists to create personalized treatment plans based on specific patient goals and functional requirements.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
              Clinical Applications
            </h2>

            <div className="space-y-8 mb-12">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Stroke Recovery
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Helps stroke survivors regain hand and finger function through repetitive, task-oriented training that promotes neuroplasticity and motor learning.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Traumatic Brain Injury
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Supports rehabilitation of hand function following TBI, with adjustable difficulty levels to match patient recovery stages.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Spinal Cord Injury
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Provides intensive hand therapy for individuals with incomplete spinal cord injuries to maximize functional independence.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Multiple Sclerosis
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Assists MS patients in maintaining and improving hand dexterity and fine motor control through regular, structured therapy.
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
              Treatment Approach
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              The Amadeo treatment process follows a systematic approach to ensure optimal outcomes:
            </p>

            <ol className="space-y-4 text-gray-700 leading-relaxed mb-12">
              <li><strong>Assessment</strong> — Initial evaluation of hand function and range of motion</li>
              <li><strong>Customization</strong> — Personalized therapy program based on patient needs</li>
              <li><strong>Training</strong> — Regular therapy sessions with progressive difficulty</li>
              <li><strong>Monitoring</strong> — Continuous progress tracking and program adjustment</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
